import { View } from 'tamagui';
import PSText from './PSText';

/**
 * PSBadge — a small pill-tag label (atom).
 *
 * Four looks, all purely presentational — a badge labels the thing it sits next
 * to and carries no press behavior:
 *   - `brand` (default): the "Access Pass only" tag — a `$brand` purple chip
 *     with a white label. Reads as a highlight/feature.
 *   - `neutral`: a dark chip (`$textDark` fill, white label) for a quiet,
 *     non-highlight state like a sold-out tier. We use the dark fill rather than
 *     `$backgroundSecondary` on purpose — a 93%-grey chip on the 97%-grey card
 *     is only a 4% step and would be nearly invisible; the dark chip stays
 *     legible and reads as "inactive/closed" without competing with the purple.
 *   - `outline`: a white chip with a black hairline outline and black label.
 *   - `trial`: a `$trialBadge` pink chip with a white label, for Trial Pass.
 *
 * `size` is a separate axis from `variant`: `large` steps up the label (`$base`
 * type instead of `Tags`' default) and the padding (`$lg`/`$sm` instead of
 * `$md`/`$xs`) so any look reads as a stronger call-out.
 *
 * `disabled` is a separate axis from `variant`: it just dims whichever look is
 * chosen, so a badge can step back when it shares a row with a more important
 * one (a sold-out Access-Pass tier: the "Sold out" badge should own attention,
 * so the "Access Pass only" tag is dimmed). It's a flag rather than a variant
 * because emphasis is orthogonal to colour — either look can be dimmed. We dim
 * with opacity, not a muted-colour token — "mute a bit" is exactly what opacity
 * is for, and adding a token is a deliberate act we don't need here.
 *
 * The design's 10px / 3px values snap up to our smallest scale steps (`$small`
 * font, `$xs` vertical pad): the token system intentionally defines only three
 * font sizes, so a tag label rounds to `$small` rather than earning a new step.
 */
export type PSBadgeVariant = 'brand' | 'neutral' | 'outline' | 'trial';
export type PSBadgeSize = 'default' | 'large';

type PSBadgeProps = {
  label: string;
  variant?: PSBadgeVariant;
  size?: PSBadgeSize;
  disabled?: boolean;
};

const LOOK = {
  brand: { bg: '$brand', textColor: '$textWhite' },
  neutral: { bg: '$backgroundSecondary', textColor: '$textDark' },
  outline: { bg: '$textWhite', textColor: '$textDark' },
  trial: { bg: '$trialBadge', textColor: '$textWhite' },
} as const;

// Dimmed, a badge reads as "still here, but not the point right now".
const DISABLED_OPACITY = 0.4;

export default function PSBadge({
  label,
  variant = 'brand',
  size = 'default',
  disabled = false,
}: PSBadgeProps) {
  const { bg, textColor } = LOOK[variant];
  const outline = variant === 'outline';
  const large = size === 'large';
  return (
    <View
      backgroundColor={bg}
      borderColor={outline ? '$borderColor' : undefined}
      borderWidth={outline ? '$borderWidth.hairline' : 0}
      opacity={disabled ? DISABLED_OPACITY : 1}
      rounded="$full"
      px={large ? '$lg' : '$md'}
      py={large ? '$sm' : '$xs'}
      alignSelf="baseline"
    >
      {large ? (
        <PSText.Tags color={textColor} fontSize="$base" lineHeight={20}>
          {label}
        </PSText.Tags>
      ) : (
        <PSText.Tags color={textColor}>{label}</PSText.Tags>
      )}
    </View>
  );
}
